import { concat } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didUpdate } from '@ember/render-modifiers';
import { type Registry as Services, service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import type { HasMany } from '@ember-data/model';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismComboSelectInput, PrismIcon } from '@mylighthouse/prism-ember';
import { filterBy } from '@nullvoxpopuli/ember-composable-helpers';
import { and, eq, gt } from 'ember-truth-helpers';
import GeneralDropdownsTriggersTriggerDropdownIconButton from 'lumen/components/general/dropdowns/triggers/trigger-dropdown-icon-button';
import MarketCustomReportingSidebarDashboard from 'lumen/components/market/custom-reporting/sidebar/dashboard';
import type { CategoryType } from 'lumen/controllers/market/custom-reporting';
import add from 'lumen/helpers/add';
import type { LookerDashboard, LookerFolder } from 'lumen/models';
import { isFolderOpen } from 'lumen/utils/custom-reporting/helpers';

interface MarketCustomReportingSidebarFolderSignature {
  Args: {
    dashboards?: HasMany<LookerDashboard>;
    folder: LookerFolder;
    folders?: LookerFolder[] | HasMany<LookerFolder>;
    isOpen: boolean;
    level: number;
    selectFolder?: (folder: LookerFolder) => void;
    selectedDashboard?: LookerDashboard;
    selectedFolder?: LookerFolder;
    title: string;
    type?: CategoryType;
    viewOnly: boolean;
  };
  Element: HTMLDivElement;
}

export default class MarketCustomReportingSidebarFolder extends Component<MarketCustomReportingSidebarFolderSignature> {
  @service declare access: Services['access'];
  @service declare customReporting: Services['custom-reporting'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];

  @tracked isOpen = false;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get canCreate() {
    const { folder, type } = this.args;

    if (!folder.canEdit) {
      return false;
    }

    return (
      type !== 'templates' &&
      !folder.isEmbedSharedRoot &&
      this.access.hasCustomReportingEdit
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get canEdit() {
    const { folder, type, level } = this.args;

    if (!this.access.hasCustomReportingEdit) {
      return false;
    }

    if (type === 'templates') {
      return false;
    }

    // if the current folder is a direct child of Portfolios, disable edit options
    if (
      level === 1 &&
      folder.belongsTo('folder').value() &&
      folder.folder.isEmbedSharedRoot
    ) {
      return false;
    }

    if (!folder.canEdit) {
      return false;
    }

    return level > 0;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get createOptions() {
    return [
      { id: 'dashboard', label: this.intl.t('custom_reporting.new_dashboard') },
      { id: 'folder', label: this.intl.t('custom_reporting.new_folder') },
    ] as const;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get dashboards() {
    return this.args.dashboards
      ?.filter((dashboard) => !dashboard.isNew)
      .sort((a, b) => a.title.localeCompare(b.title));
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get editOptions() {
    return [
      { id: 'move', label: this.intl.t('custom_reporting.actions.move') },
      { id: 'rename', label: this.intl.t('custom_reporting.actions.rename') },
      { id: 'delete', label: this.intl.t('custom_reporting.actions.delete') },
    ] as const;
  }

  get folders(): LookerFolder[] {
    const { folders } = this.args;

    if (!folders) {
      return [];
    }

    return folders;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isActive() {
    const { viewOnly, selectedFolder, folder } = this.args;

    if (this.isTemplates) {
      return (
        this.router.currentRouteName === 'market.custom-reporting.templates'
      );
    }

    return viewOnly && selectedFolder?.id === folder.id;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isTemplates() {
    return this.args.type === 'templates' && this.args.level === 0;
  }

  constructor(
    owner: Owner,
    args: MarketCustomReportingSidebarFolderSignature['Args'],
  ) {
    super(owner, args);

    this.checkOpenState();
  }

  @action checkOpenState(): void {
    const { folder, selectedDashboard, selectedFolder, isOpen } = this.args;

    if (this.isTemplates) {
      this.isOpen = true;
      return;
    }

    if (selectedFolder) {
      this.isOpen = isFolderOpen(folder, selectedFolder);
      return;
    }

    if (selectedDashboard && !selectedDashboard.isDeleted) {
      this.isOpen = isFolderOpen(folder, selectedDashboard);
      return;
    }

    this.isOpen = isOpen;
  }

  @action onCreateOptionSelect(
    option: (typeof this.createOptions)[number],
  ): void {
    const { folder } = this.args;

    switch (option.id) {
      case 'dashboard':
        this.customReporting.createDashboard(folder);
        break;
      case 'folder':
        this.customReporting.createFolder(folder);
        break;
      default:
        option satisfies never;
    }
  }

  @action onEditOptionSelect(option: (typeof this.editOptions)[number]): void {
    const { folder } = this.args;

    switch (option.id) {
      case 'rename':
        this.customReporting.editFolder(folder);
        break;
      case 'delete':
        this.customReporting.deleteFolder(folder);
        break;
      case 'move':
        this.customReporting.moveFolder(folder);
        break;
      default:
        option satisfies never;
    }
  }

  @action toggleOpen(): void {
    if (this.isTemplates) {
      this.router.transitionTo('market.custom-reporting.templates');
      return;
    }

    this.isOpen = !this.isOpen;
    this.args.selectFolder?.(this.args.folder);
  }

  <template>
    <div
      class="sidebar-item text-300--regular flex items-center pr-100 pl-200
        {{if this.isActive 'sidebar-item--active'}}
        {{if this.isOpen 'sidebar-item--is-open'}}"
      style={{htmlSafe (concat "--level:" @level)}}
      {{didUpdate
        this.checkOpenState
        @selectedDashboard.id
        @selectedDashboard.folder.id
      }}
      ...attributes
    >
      <button
        class="button--unstyled folder-button flex w-full flex-row items-center gap-200"
        title={{if (gt @level 0) @title}}
        type="button"
        {{on "click" this.toggleOpen}}
      >
        {{#if this.isTemplates}}
          <PrismIcon @name="templates" @size="small" />
        {{else}}
          <PrismIcon
            @name={{if this.isOpen "chevron-down" "chevron-right"}}
            @size="small"
          />
        {{/if}}
        {{#unless (eq @level 0)}}
          <PrismIcon
            @name={{if this.isOpen "folder-outline" "folder"}}
            @size="small"
          />
        {{/unless}}
        <span class="truncate">
          {{@title}}
        </span>
      </button>
      {{#unless @viewOnly}}
        <div
          class="actions ml-auto flex items-center gap-100"
          data-test-id="actions"
        >
          {{#if this.canEdit}}
            {{#let
              (component
                GeneralDropdownsTriggersTriggerDropdownIconButton
                buttonType="ghost"
                icon="meatballs"
                small=true
              )
              as |triggerComponent|
            }}
              <PrismComboSelectInput
                @horizontalPosition="left"
                @onChange={{this.onEditOptionSelect}}
                @options={{this.editOptions}}
                @unsafe_dropdownClass="custom-reporting--actions-dropdown"
                @unsafe_triggerComponent={{triggerComponent}}
                data-test-id="edit-options"
              />
            {{/let}}
          {{/if}}
          {{#if this.canCreate}}
            {{#let
              (component
                GeneralDropdownsTriggersTriggerDropdownIconButton
                buttonType="ghost"
                icon="plus"
                small=true
              )
              as |triggerComponent|
            }}
              <PrismComboSelectInput
                @horizontalPosition="left"
                @onChange={{this.onCreateOptionSelect}}
                @options={{this.createOptions}}
                @unsafe_triggerComponent={{triggerComponent}}
                data-test-id="create-options"
              />
            {{/let}}
          {{/if}}
        </div>
      {{/unless}}
      {{#if (and @viewOnly (eq @selectedFolder.id @folder.id))}}
        <PrismIcon
          @name="checkmark"
          @size="small"
          data-test-id="folder-selected"
        />
      {{/if}}
    </div>

    {{#if this.isOpen}}
      <div
        class="sidebar-items-wrapper {{if (gt @level 0) 'show-guide'}}"
        data-test-id="folder-items"
        style={{htmlSafe (concat "--level:" @level)}}
      >
        {{#each (filterBy "isNew" false this.folders) as |folder|}}
          <MarketCustomReportingSidebarFolder
            @dashboards={{folder.dashboards}}
            @folder={{folder}}
            @folders={{folder.folders}}
            @isOpen={{false}}
            @level={{add @level 1}}
            @selectFolder={{@selectFolder}}
            @selectedDashboard={{@selectedDashboard}}
            @selectedFolder={{@selectedFolder}}
            @title={{folder.name}}
            @type={{@type}}
            @viewOnly={{@viewOnly}}
            class="folder-item folder-item--nested"
            data-test-id={{concat "folder-" folder.id}}
          />
        {{/each}}

        {{#each this.dashboards as |dashboard|}}
          <MarketCustomReportingSidebarDashboard
            @dashboard={{dashboard}}
            @level={{add @level 1}}
            @selectedDashboard={{@selectedDashboard}}
            @type={{@type}}
            @viewOnly={{@viewOnly}}
          />
        {{/each}}
      </div>
    {{/if}}
  </template>
}
